/**
 * <card-gallery> — lightweight image/video gallery on the product card.
 */
if (!customElements.get("card-gallery")) {
  customElements.define(
    "card-gallery",
    class CardGallery extends HTMLElement {
      connectedCallback() {
        if (!this.hasAttribute("data-card-gallery")) return;

        this.track = this.querySelector("[data-gallery-track]");
        if (!this.track) return;

        this.slides = Array.from(this.track.children);

        if (window.matchMedia("(pointer: coarse)").matches) {
          this.track.classList.add("swiper-no-swiping");
          const stopStart = (e) => e.stopPropagation();
          this.track.addEventListener("pointerdown", stopStart);
          this.track.addEventListener("touchstart", stopStart, { passive: true });
        }

        this.prevBtn = this.querySelector("[data-gallery-prev]");
        this.nextBtn = this.querySelector("[data-gallery-next]");
        this.buttons = this.querySelector("[data-gallery-buttons]");
        this.indicator = this.querySelector("[data-gallery-indicator]");
        this.status = this.querySelector("[data-gallery-status]");
        this.ofWord = this.dataset.galleryOf || "/";
        this.segments = Array.from(
          this.querySelectorAll("[data-gallery-indicator] > *"),
        );
        this.index = 0;
        this.slideWidth = 0;
        this._ticking = false;
        this._announce = false;

        this.onScroll = this.onScroll.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.track.addEventListener("scroll", this.onScroll, { passive: true });
        this.addEventListener("keydown", this.onKeydown);

        this.prevBtn?.addEventListener("click", (e) => this.onArrow(e, -1));
        this.nextBtn?.addEventListener("click", (e) => this.onArrow(e, 1));

        if ("ResizeObserver" in window) {
          this._resizeObserver = new ResizeObserver(() => this.layout());
          this._resizeObserver.observe(this.track);
        }

        this.layout();
        this.update();
        this._announce = true;
      }

      disconnectedCallback() {
        this.track?.removeEventListener("scroll", this.onScroll);
        this.removeEventListener("keydown", this.onKeydown);
        this._resizeObserver?.disconnect();
      }

      layout() {
      }

      onArrow(e, dir) {
        e.preventDefault();
        this.goTo(this.index + dir);
        if (e.detail !== 0) e.currentTarget.blur();
      }

      onKeydown(e) {
        if (this.slides.length < 2) return;
        if (e.altKey || e.ctrlKey || e.metaKey) return;
        // don't hijack keys typed inside the quick-buy picker
        if (e.target.closest(".card-pro__qbp")) return;
        let target;
        switch (e.key) {
          case "ArrowLeft":
            target = this.index - 1;
            break;
          case "ArrowRight":
            target = this.index + 1;
            break;
          case "Home":
            target = 0;
            break;
          case "End":
            target = this.slides.length - 1;
            break;
          default:
            return;
        }
        e.preventDefault();
        this.goTo(target);
      }

      onScroll() {
        if (this._ticking) return;
        this._ticking = true;
        requestAnimationFrame(() => {
          const width = this.slideWidth || this.track.clientWidth || 1;
          const next = Math.round(this.track.scrollLeft / width);
          if (next !== this.index) {
            this.index = next;
            this.update();
          }
          this._ticking = false;
        });
      }

      goTo(target) {
        const last = this.slides.length - 1;
        const index = Math.max(0, Math.min(target, last));
        const width = this.slideWidth || this.track.clientWidth;
        const reduce = window.matchMedia(
          "(prefers-reduced-motion: reduce)",
        ).matches;
        this.track.scrollTo({
          left: index * width,
          behavior: reduce ? "auto" : "smooth",
        });
      }

      update() {
        const last = this.slides.length - 1;
        const single = this.slides.length < 2;
        this.segments.forEach((seg, i) =>
          seg.classList.toggle("is-active", i === this.index),
        );
        const prevDisabled = this.index <= 0;
        const nextDisabled = this.index >= last;
        // Keep keyboard focus alive when the focused arrow reaches an end and is
        // about to be disabled — hand focus to the opposite (still enabled) arrow.
        if (prevDisabled && this.prevBtn && document.activeElement === this.prevBtn) {
          this.nextBtn?.focus();
        } else if (
          nextDisabled &&
          this.nextBtn &&
          document.activeElement === this.nextBtn
        ) {
          this.prevBtn?.focus();
        }
        this.prevBtn?.toggleAttribute("disabled", prevDisabled);
        this.nextBtn?.toggleAttribute("disabled", nextDisabled);
        this.buttons?.classList.toggle("card-pro__gallery-buttons--hidden", single);
        this.indicator?.classList.toggle(
          "card-pro__gallery-indicator--hidden",
          single,
        );
        if (this.status && this._announce && !single) {
          this.status.textContent = `${this.index + 1} ${this.ofWord} ${this.slides.length}`;
        }
      }

      refresh() {
        if (!this.track) return;
        this.slides = Array.from(this.track.children);
        this.segments = Array.from(
          this.querySelectorAll("[data-gallery-indicator] > *"),
        );
        this.index = 0;
        this.slideWidth = 0;
        this.track.scrollLeft = 0;
        this.layout();
        this.update();
      }
    },
  );
}

/* -------------------------------------------------------------------------
 * Lazy card video bootstrap.
 *
 * This is the SAME logic that lives inline in snippets/card-pro-gallery-slides.liquid.
 * It is duplicated here because sections that inject their cards via
 * element.innerHTML (e.g. sections/product-recommendations.liquid, which fetches
 * its markup over AJAX) can never execute that inline <script> — browsers do not
 * run script tags inserted via innerHTML. As a result card videos in those
 * sections kept their data-src and never loaded.
 *
 * card-gallery.js is loaded with a normal <script src> tag on every page that
 * uses card-pro, so placing the bootstrap here guarantees it runs.
 *
 * The window.__cardLazyVideo guard below is the same one the inline copy uses,
 * so whichever runs first wins and the other becomes a no-op. Sections that
 * already work today are therefore unaffected.
 *
 * PERF FIX (Aug 2026): when a video leaves the viewport before it has buffered
 * enough to play (readyState < HAVE_FUTURE_DATA), its download is now cancelled
 * (src removed + load()) and it is returned to the lazy pool. Previously a fast
 * scroll through the page started dozens of concurrent MP4 downloads that were
 * never aborted, which choked the renderer on desktop (page freeze / dead
 * scroll). Re-entering the viewport reloads the video from data-src as before.
 * ---------------------------------------------------------------------- */
(function () {
  if (window.__cardLazyVideo) return;
  window.__cardLazyVideo = true;

  function boot() {
    var io = new IntersectionObserver(
      function (es) {
        es.forEach(function (e) {
          var v = e.target;
          if (!e.isIntersecting) {
            if (!v.paused) v.pause();
            // Cancel in-flight downloads for videos that scrolled away before
            // becoming playable; keep already-buffered ones (readyState >= 3).
            if (v.dataset.loaded && v.readyState < 3) {
              v.querySelectorAll("source[src]").forEach(function (s) {
                s.removeAttribute("src");
              });
              v.load(); // resets the element and aborts the network fetch
              delete v.dataset.loaded;
            }
            return;
          }
          if (!v.dataset.loaded) {
            v.querySelectorAll("source[data-src]").forEach(function (s) {
              s.src = s.dataset.src;
            });
            v.load();
            v.dataset.loaded = "1";
          }
          var p = v.play();
          if (p && p.catch) p.catch(function () {});
        });
      },
      { rootMargin: "200px 0px" },
    );

    function scan() {
      document.querySelectorAll("video.js-lazy-video").forEach(function (v) {
        io.observe(v);
      });
    }

    scan();
    new MutationObserver(scan).observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
